import { Component, DoCheck, inject, input, viewChildren, effect } from '@angular/core';
import { ExportCsvService } from 'src/app/services/export-csv.service';
import { CsvModes } from 'src/app/core/csv-modes.class';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTable, MatTableModule } from '@angular/material/table';
import { DealHierarchy } from 'src/app/models/classes/deal-hierarchy.class';
import { TableLogicService } from 'src/app/services/table-logic.service';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InfoIconComponent } from 'src/app/shared/info-icon/info-icon.component';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { TvpiValueFormatPipe } from 'src/app/pipes/tvpi-value-format.pipe';
import { IrrValueFormatPipe } from 'src/app/pipes/irr-value-format.pipe';
import { SortByPipe } from 'src/app/pipes/sortBy.pipe';
import { MatIconModule } from '@angular/material/icon';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import {MatSortModule} from '@angular/material/sort';
import { AggregatedPortfolioSeries } from 'src/app/models/classes/aggregated-portfolio-series.class';
import { PortfolioSeries } from 'src/app/models/classes/portfolio-series.class';
import { InvestmentTypeHierarchy } from 'src/app/models/classes/investment-type-hierarchy.class';
import { Portfolio } from 'src/app/models/classes/portfolio.class';
import { SeriesTableConfigService } from '../../services/series-table-config.service';

@Component({
    selector: 'app-series-qroll-table',
    imports: [
        CommonModule,
        MatTooltipModule,
        InfoIconComponent,
        CurrencyFormatPipe,
        TvpiValueFormatPipe,
        MatTableModule,
        IrrValueFormatPipe,
        SortByPipe,
        RouterModule,
        MatIconModule,
        TourMatMenu,
        MatSortModule
    ],
    providers: [TvpiValueFormatPipe, IrrValueFormatPipe],
    templateUrl: './series-qroll-table.component.html',
    styleUrls: ['./series-qroll-table.component.scss']
})
export class SeriesQRollTableComponent implements DoCheck {
  private tableLogicService = inject(TableLogicService);
  private exportCsvService = inject(ExportCsvService);
  private tvpiValueFormatPipe = inject(TvpiValueFormatPipe);
  private irrValueFormatPipe = inject(IrrValueFormatPipe);
  activatedRoute = inject(ActivatedRoute);
  activityTracking = inject(ActivityTrackingService);
  private seriesTableConfig = inject(SeriesTableConfigService);
  protected readonly tableConfig = this.seriesTableConfig.config;

  readonly iconSet = input<Record<string, string> | undefined>(undefined);
  readonly investmentTypes = input<InvestmentTypeHierarchy[]>();
  readonly portfolioSummary = input<Portfolio>();
  readonly summary = input<AggregatedPortfolioSeries | PortfolioSeries>();
  readonly portfolioName = input<string>('');
  readonly seriesName = input<string>('');
  readonly investorId = input<number | undefined>(undefined);
  readonly seriesId = input<number | undefined>(undefined);
  readonly reportingPeriod = input<string | undefined>(undefined);
  readonly isAggregatedSeries = input<boolean>(false);
  QRollData : InvestmentTypeHierarchy[] = [];
  sortingInitialized = false;
  displayedColumns: string[] = [];
  private static readonly multipleRowHeaders = ['name', 'realizationType', 'priorNAV', 'nav'];
  headerColumns: string[] = [];
  displayedHeaders: string[] = [];
  innerDisplayedColumns: string[] = [];

  constructor() {
    effect(() => {
      const cfg = this.seriesTableConfig.config();
      this.displayedColumns = cfg.qRoll.columns;
      this.headerColumns = cfg.qRoll.columns.filter(
        (column) => !SeriesQRollTableComponent.multipleRowHeaders.includes(column),
      );
      this.innerDisplayedColumns = cfg.qRoll.innerColumns;
      this.displayedHeaders = cfg.qRoll.headers;
    });
  }
  sorts = viewChildren(MatSort);
  dataSources: MatTableDataSource<DealHierarchy>[] = [];
  readonly innerTables = viewChildren<MatTable<DealHierarchy>>('innerTables');
  isExpandedSeries = true;

  private readonly rebuildQRollData = effect(() => {
    const investmentTypes = this.investmentTypes();
    if (investmentTypes) {
      this.QRollData = [...investmentTypes];
      this.formatQRollTableValues();
      this.sortingInitialized = false;
    }
  });

  ngDoCheck() {
    this.initializeSorting();
  }

  initializeSorting() {
    if (!this.sortingInitialized) {
      if (this.sorts().length > 0) {
        this.dataSources.forEach((x, index) => {
          x.sort = this.sorts().find((_, i) => i == index)
        });
        this.sortingInitialized = true;
      }
    }
  }

  formatQRollTableValues() {
    this.QRollData = this.tableLogicService.sortDataForSeriesTable(this.QRollData);
    this.QRollData.forEach((investmentType) => {
      investmentType['isExpanded'] = true;
      this.dataSources[investmentType['priority'] - 1] = new MatTableDataSource(investmentType.deals);
      investmentType.deals.forEach(deal => {
        deal.showHoldings = this.tableLogicService.checkDealToHoldingMapping(deal.holdings);
        deal.disableDrillThrough = this.tableLogicService.isDealDrillThroughDisabled(deal);
      })
    });
  }

  exportQRollDataToCsv(event: MouseEvent) {
    const seriesName = this.seriesName() ?? '';
    this.activityTracking.exportToExcelClick('series qroll table', seriesName);
    (event.target as HTMLButtonElement).disabled = true;
    const cfg = this.seriesTableConfig.config();
    const qrollForwardDealsArray = [], qrollForwardHoldingsArray = [], invTypeArray = [];
    const filename = this.exportCsvService.getExportFilename(CsvModes.quarterlyRollForwardDeals, this.reportingPeriod() ?? '', this.portfolioName() ?? '', seriesName);
    const headerForQRollForwardDeals = this.exportCsvService.getCsvHeader(CsvModes.quarterlyRollForwardDeals);
    const headerForQRollForwardHoldings = this.exportCsvService.getCsvHeader(CsvModes.quarterlyRollForwardHoldings);
    const { invTypeQoqTvpiChange, invTypeQoqIrrChange, ...invTypeHeaderWithoutPerformance } = this.exportCsvService.getCsvHeader(CsvModes.qRollForwardInvestmentType);
    const headerForInvestmentType = cfg.showSubtotalPerformance
      ? { ...invTypeHeaderWithoutPerformance, invTypeQoqTvpiChange, invTypeQoqIrrChange }
      : invTypeHeaderWithoutPerformance;
    qrollForwardDealsArray.push(headerForQRollForwardDeals);
    qrollForwardHoldingsArray.push(headerForQRollForwardHoldings);
    invTypeArray.push(headerForInvestmentType);
    (this.investmentTypes() ?? []).forEach(invType => {
      const invTypeData: Record<string, unknown> = {};
      invTypeData['invType'] = invType.name;
      invTypeData['invTypePriorNav'] = invType.priorNAV;
      invTypeData['invTypeQoqContributions'] = invType.qoQContributions;
      invTypeData['invTypeQoqDistributions'] = invType.qoQDistributions;
      invTypeData['invTypeGainLoss'] = invType.qoQGainLoss;
      invTypeData['invTypeCurrentNav'] = invType.nav;
      if (cfg.showSubtotalPerformance) {
        invTypeData['invTypeQoqTvpiChange'] = this.tvpiValueFormatPipe.transform(invType.qoQTvpiChange);
        invTypeData['invTypeQoqIrrChange'] = this.irrValueFormatPipe.transform(invType.qoQIrrChange);
      }
      invTypeArray.push(invTypeData);

      const sortedDeals = this.exportCsvService.sortArrayAscending(invType.deals);
      sortedDeals.forEach((deal: DealHierarchy) => {
        const dealData: Record<string, unknown> = {};
        dealData['invType'] = invType.name;
        dealData['dealName'] = deal.name;
        dealData['invStatus'] = deal.realizationType;
        dealData['dealPriorNav'] = deal.priorNAV;
        dealData['dealQoqContributions'] = deal.qoQContributions;
        dealData['dealQoqDistributions'] = deal.qoQDistributions;
        dealData['dealGainLoss'] = deal.qoQGainLoss;
        dealData['dealCurrentNav'] = deal.nav;
        dealData['dealQoqTvpiChange'] = this.tvpiValueFormatPipe.transform(deal.qoQTvpiChange);
        dealData['dealQoqIrrChange'] = this.irrValueFormatPipe.transform(deal.qoQIrrChange);
        qrollForwardDealsArray.push(dealData);
        const sortedHoldings = this.exportCsvService.sortArrayAscending(deal.holdings);
        sortedHoldings.forEach((holding: DealHierarchy['holdings'][number]) => {
          const data: Record<string, unknown> = {};
          data['invType'] = invType.name;
          data['dealName'] = deal.name;
          data['holdingName'] = holding.name;
          data['invStatus'] = deal.realizationType;
          data['holdingPriorNav'] = holding.priorNAV;
          data['holdingQoqContributions'] = holding.qoQContributions;
          data['holdingQoqDistributions'] = holding.qoQDistributions;
          data['holdingGainLoss'] = holding.qoQGainLoss;
          data['holdingCurrentNav'] = holding.nav;
          qrollForwardHoldingsArray.push(data);
        })
      })
    })
    const worksheets = [{ name: 'Q Roll Forward - Deals', csvArray: qrollForwardDealsArray }, { name: 'Q Roll Forward - Holdings', csvArray: qrollForwardHoldingsArray }, { name: 'Investment Type Subtotals', csvArray: invTypeArray }]
    this.exportCsvService.exportToExcelFile(filename, worksheets);
    (event.target as HTMLButtonElement).disabled = false;
  }

  scrollQRTableHandler(event: Event, investment: InvestmentTypeHierarchy) {
    if ((event.target as HTMLElement).scrollLeft > 0) {
      investment['displayShadowOnQRTable'] = true;
    } else {
      investment['displayShadowOnQRTable'] = false;
    }
  }

  expandTable(investment: InvestmentTypeHierarchy) {
    investment.isExpanded = !investment.isExpanded;
    if (investment.isExpanded) {
      this.sortingInitialized = false;
      this.initializeSorting();
    }
    this.activityTracking.expandOrCollapseToggled('investment', investment, 'series qroll table', investment.isExpanded)
  }
}
